import {
  breakpointSizes,
  ButtonDesigns,
  ButtonTypes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectCreditCardIdFromRoute,
  selectCreditCardUser,
} from 'src/app/Budget/redux/budget.selectors'
import AccountHeader from 'src/components/AccountHeader/AccountHeader'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import Box from 'src/components/Box/Box'
import Button from 'src/components/Button/Button'
import { CreateNewDropdownOption } from 'src/components/CreateNewDropdown/CreateNewDropdown'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import Subnav, { SubnavDesign } from 'src/components/Subnav/Subnav'
import SvgStatusSummaryActionRequiredRed from 'src/components/Svgs/SvgStatusSummaryActionRequiredRed'
import SvgTrash from 'src/components/Svgs/SvgTrash'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import TeamAvatarsIcon from 'src/components/TeamAvatarsIcon/TeamAvatarsIcon'
import Topnav from 'src/components/Topnav/Topnav'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import {
  OrganizationInviteModel,
  OrganizationsListResponse,
} from 'src/models/organizations.models'
import BudgetAppRoute from 'src/navigation/appRoutes/budget.appRoute'
import { Rdrct } from 'src/navigation/Lnk'
import {
  budgetHistoryUrl,
  budgetOverviewUrl,
  budgetTransactionsUrl,
  budgetVirtualCardsUrl,
  deactivateBudgetUrl,
  manageOrgInviteUrl,
  updateBudgetUrl,
} from 'src/navigation/urls'
import {
  useQueryCreditCard,
  useQueryCreditCardPermissions,
} from 'src/queries/creditCard.queries'
import { createSetIsSetupOrganizationPopupOpen } from 'src/redux/actions/app.actions'
import {
  selectIsUserOrglessRCNUser,
  selectNullSafeLocaleFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectOrganizationInvitesModelsFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectLoadingFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import {
  selectBudgetHistoryMatch,
  selectBudgetOverviewMatch,
  selectBudgetTransactionsMatch,
  selectBudgetVirtualCardsMatch,
  selectCalculatedBackgroundLocation,
  selectLocationFromState,
} from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

const Budget: React.FC = () => {
  const creditCardId = useSelector(selectCreditCardIdFromRoute)

  const { data: creditCard, isLoading: isLoadingCreditCard } =
    useQueryCreditCard({
      creditCardId: creditCardId,
    })
  const {
    data: creditCardPermissions,
    isLoading: isLoadingCreditCardPermissions,
  } = useQueryCreditCardPermissions({
    creditCardId: creditCardId,
    retry: false,
  })

  const creditCardUser = useSelector(selectCreditCardUser)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const budgetOverviewMatch = useSelector(selectBudgetOverviewMatch)
  const budgetVirtualCardsMatch = useSelector(selectBudgetVirtualCardsMatch)
  const budgetTransactionsMatch = useSelector(selectBudgetTransactionsMatch)
  const budgetHistoryMatch = useSelector(selectBudgetHistoryMatch)
  const isUserOrglessRCNUser = useSelector(selectIsUserOrglessRCNUser)
  const currentLocation = useSelector(selectLocationFromState)
  const loadingRemoteConfig = useSelector(selectLoadingFromRemoteConfigState)
  const orgInvites = useSelector(selectOrganizationInvitesModelsFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)

  let budgetAndInviteOrg: OrganizationsListResponse | undefined
  let correspondingInviteId
  if (creditCard !== undefined) {
    Object.values(orgInvites).forEach((invite: OrganizationInviteModel) => {
      if (invite.organization.id === creditCard.organizationId) {
        budgetAndInviteOrg = invite.organization
        correspondingInviteId = invite.id
      }
    })
  }

  const { analytics } = useAnalyticsContext()

  const canUpdateCreditCard = creditCardPermissions?.canUpdateCreditCard
  const canIssueVirtualCards = creditCardPermissions?.canIssueVirtualCards
  const canViewBudget = creditCardPermissions?.canRead

  const dispatch = useBackgroundDispatch()

  const subnavOptions = React.useMemo(() => {
    if (!creditCard) return []

    return [
      {
        label: <Trns source="Overview" />,
        to: budgetOverviewUrl(creditCard.id),
        isActive: !!budgetOverviewMatch,
        ariaLabelSource: 'Overview',
        'data-testid': 'overviewTab',
      },
      {
        label: <Trns source="Virtual Cards" />,
        to: budgetVirtualCardsUrl(creditCard.id),
        isActive: !!budgetVirtualCardsMatch,
        ariaLabelSource: 'Virtual Cards',
        'data-testid': 'virtualCardsTab',
      },
      {
        label: <Trns source="Transactions" />,
        to: budgetTransactionsUrl(creditCard.id),
        isActive: !!budgetTransactionsMatch,
        ariaLabelSource: 'Transactions',
        'data-testid': 'transactionsTab',
      },
      {
        label: <Trns source="History" />,
        to: budgetHistoryUrl(creditCard.id),
        isActive: !!budgetHistoryMatch,
        ariaLabelSource: 'History',
        'data-testid': 'historyTab',
      },
    ]
  }, [
    creditCard?.id,
    budgetOverviewMatch,
    budgetVirtualCardsMatch,
    budgetTransactionsMatch,
    budgetHistoryMatch,
  ])

  const updateBudgetTopnavOption: CreateNewDropdownOption[] | undefined =
    creditCard !== undefined && canUpdateCreditCard
      ? isUserOrglessRCNUser
        ? [
            {
              label: <Trns source={'Update Budget'} />,
              to: currentLocation.pathname,
              onClick: (): void => {
                dispatch(
                  createSetIsSetupOrganizationPopupOpen(
                    true,
                    updateBudgetUrl(creditCard.id),
                  ),
                )
              },
              ariaLabelSource: 'Update Budget',
              image: <SvgUpdate />,
            },
          ]
        : [
            {
              label: <Trns source={'Update Budget'} />,
              to: updateBudgetUrl(creditCard.id),
              onClick: (): void => {
                analytics?.track(
                  'budget detail header - open update budget form - button press',
                  {
                    intent: 'open update budget form',
                    interaction: 'button press',
                    screen: budgetOverviewUrl(':id'),
                  },
                )
              },
              ariaLabelSource: 'Update Budget',
              image: <SvgUpdate />,
            },
          ]
      : undefined
  const positiveActionOptions = [...(updateBudgetTopnavOption || [])]

  const negativeActionOptions =
    creditCard !== undefined && canUpdateCreditCard && creditCard?.isActive
      ? [
          {
            label: <Trns source={'Deactivate Budget'} />,
            to: deactivateBudgetUrl(creditCard.id),
            onClick: (): void => {
              analytics?.track('deactivate budget', {
                budget_id: creditCard.id,
                issuer: creditCard.issuer,
              })
            },
            ariaLabelSource: translateSingleString(locale, 'Deactivate Budget'),
            image: <SvgTrash />,
          },
        ]
      : []

  const isLoading = (): boolean | undefined =>
    isLoadingCreditCard || isLoadingCreditCardPermissions

  return isLoading() || loadingRemoteConfig ? (
    <LoadingBackgroundPage />
  ) : creditCard !== undefined ? (
    <BackgroundPage>
      <Topnav
        titleElements={{
          title:
            (creditCard.budgetUsers?.length ?? 0) > 1 ? (
              <div
                css={css`
                  display: flex;
                  flex-direction: row;
                  gap: 8px;
                `}
              >
                <TeamAvatarsIcon users={creditCard.budgetUsers} />
                <span
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.black,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  <Trns source={'Team Budget'} />
                </span>
              </div>
            ) : !canIssueVirtualCards && creditCardUser !== undefined ? (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                `}
              >
                <UserAvatar
                  size={AvatarSizes.Small}
                  user={creditCardUser}
                  css={css`
                    margin-right: 10px;
                  `}
                />
                <Trns
                  source={"${firstName}'s Budget"}
                  expressions={{
                    firstName: creditCardUser.firstNameOrEmail,
                  }}
                />
              </div>
            ) : (
              <Trns source={'Your Budget'} />
            ),
          scrollContent: (
            <span
              css={css`
                font-size: 14px;
                line-height: 18px;
                ${!canIssueVirtualCards &&
                css`
                  margin-left: 32px;
                `}
              `}
            >
              {creditCard.truncatedDisplayName}
            </span>
          ),
        }}
        alternativeActionOptions={[
          {
            title: <Trns source={'Budget Options'} />,
            positiveActionOptions: positiveActionOptions,
            negativeActionOptions: negativeActionOptions,
          },
        ]}
      />
      <AccountHeader creditCard={creditCard} />
      <Subnav
        design={SubnavDesign.Header}
        options={!canViewBudget ? [] : subnavOptions}
      />
      {creditCardPermissions !== undefined && !canViewBudget ? (
        <Box
          css={css`
            margin-top: 40px;
            max-width: 500px;
            align-self: center;
            padding: 40px;
            display: flex;
            flex-direction: column;
            align-items: center;
          `}
        >
          <SvgStatusSummaryActionRequiredRed
            css={css`
              margin-bottom: 32px;
            `}
          />
          <div
            css={css`
              ${getFontCss(
                20,
                styleConstants.colors.red,
                styleConstants.fontFamilies.medium,
              )};
              position: relative;
              margin-bottom: 20px;
              display: flex;
              flex-direction: column;
              flex-shrink: 0;

              ${getBreakpointDown(breakpointSizes.XSM)(css`
                padding: 20px 30px;
              `)}
            `}
          >
            <Trns source={'Action Required'} />
          </div>
          <div
            css={css`
              overflow-wrap: break-word;
              text-align: center;
            `}
          >
            <Trns
              source={
                'As a budget user, you can create and update virtual cards on this budget. To access these features, ${cta}.'
              }
              expressions={{
                cta:
                  budgetAndInviteOrg !== undefined ? (
                    <Trns
                      source={'accept your invite to join ${orgName}'}
                      expressions={{ orgName: budgetAndInviteOrg.name }}
                    />
                  ) : (
                    <Trns
                      source={
                        'please reach out to the Owner or Admin to invite you to the appropriate org'
                      }
                    />
                  ),
              }}
            />
          </div>
          {correspondingInviteId !== undefined && (
            <Button
              design={ButtonDesigns.designCta}
              type={ButtonTypes.button}
              text={<Trns source={'Review the Invite'} />}
              linkProps={{
                to: manageOrgInviteUrl(correspondingInviteId),
                ariaLabelSource: 'Review the Invite',
              }}
              big={true}
              css={css`
                width: 315px;
                margin-top: 40px;
              `}
            />
          )}
        </Box>
      ) : (
        <Switch location={calculatedBackgroundLocation}>
          {BudgetAppRoute.subRoutes.map((route, i) => (
            <Route key={i} {...route.routeProps}>
              {route.component}
            </Route>
          ))}
          <Route {...BudgetAppRoute.routeProps} exact>
            <Rdrct to={budgetOverviewUrl(creditCard.id)} />
          </Route>
          <Route path={'/'}>
            <NotFoundBackgroundPageContent />
          </Route>
        </Switch>
      )}
    </BackgroundPage>
  ) : (
    <NotFoundBackgroundPage />
  )
}

export default Budget
